import type { DeskDetail, Resource } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, Download, FileText, FolderOpen, Globe, Image as ImageIcon, Loader2, Paperclip, Trash2, Upload, X } from 'lucide-react';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { FileViewer, resourceFile } from '@/components/files/file-preview';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { intlLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/** Same limit as the Control Plane (DESKWORKS_RESOURCE_MAX_FILE_MB). */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error(t('The file could not be read.')));
    reader.readAsDataURL(file);
  });
}

async function upload(workspaceId: string, file: File, place: { deskId?: string | null; conversationId?: string | null }) {
  if (file.size > MAX_FILE_BYTES) throw new Error(t('{name} is larger than 10 MB.', { name: file.name }));
  return api.resources.upload(workspaceId, { name: file.name, mimeType: file.type || undefined, contentBase64: await toBase64(file), ...place });
}

async function download(r: Pick<Resource, 'id' | 'name'>) {
  const { blob, filename } = await api.resources.download(r.id, r.name);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || r.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function ResourceIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  return mimeType.startsWith('image/') ? <ImageIcon className={className} /> : <FileText className={className} />;
}

/** What agents get from the file: its text, or why there is none. */
function textNote(r: Resource) {
  if (r.textStatus === 'ready') return t('{n} characters of text', { n: r.textChars.toLocaleString(intlLocale) });
  if (r.textStatus === 'failed') return r.textError ?? t('Text could not be read');
  return t('No text for agents (image or other binary)');
}

// ---------------------------------------------------------------- attachments (setup chat)

export interface Attachment {
  key: string;
  name: string;
  id?: string;
  failed?: boolean;
}

/**
 * Files attached to the next message of a setup chat (docs/features/resources.md): each one is uploaded right away
 * as a resource of the workspace; the message then carries their ids.
 */
export function useAttachments(workspaceId: string, conversationId?: string | null) {
  const toast = useToast();
  const [items, setItems] = useState<Attachment[]>([]);
  const add = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      setItems((list) => [...list, { key, name: file.name }]);
      upload(workspaceId, file, { conversationId: conversationId ?? null })
        .then((r) => setItems((list) => list.map((a) => (a.key === key ? { ...a, id: r.id, name: r.name } : a))))
        .catch((err) => {
          setItems((list) => list.filter((a) => a.key !== key));
          toast.error(t('{name} was not added', { name: file.name }), errorMessage(err));
        });
    }
  };
  return {
    items,
    add,
    remove: (key: string) => setItems((list) => list.filter((a) => a.key !== key)),
    clear: () => setItems([]),
    ids: items.flatMap((a) => (a.id ? [a.id] : [])),
    uploading: items.some((a) => !a.id),
  };
}

export type Attachments = ReturnType<typeof useAttachments>;

export function AttachButton({ attachments, className, disabled }: { attachments: Attachments; className?: string; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) attachments.add(e.target.files);
          e.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={className}
        disabled={disabled}
        onClick={() => input.current?.click()}
        title={t('Attach files: brand guide, price list, product docs, examples (up to 10 MB each)')}
        aria-label={t('Attach files')}
      >
        <Paperclip />
      </Button>
    </>
  );
}

export function AttachmentChips({ attachments }: { attachments: Attachments }) {
  if (!attachments.items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {attachments.items.map((a) => (
        <span key={a.key} className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs">
          {a.id ? <Paperclip className="size-3.5 shrink-0 text-muted-foreground" /> : <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />}
          <span className="truncate">{a.name}</span>
          <button type="button" onClick={() => attachments.remove(a.key)} className="text-muted-foreground hover:text-foreground" aria-label={t('Remove {name}', { name: a.name })}>
            <X className="size-3.5" />
          </button>
        </span>
      ))}
    </div>
  );
}

/** Drop files anywhere on the children to attach them. */
export function DropZone({ onFiles, children, className, label = t('Drop files to attach them') }: { onFiles: (files: FileList) => void; children: ReactNode; className?: string; label?: string }) {
  const [over, setOver] = useState(false);
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  return (
    <div
      className={cn('relative', className)}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setOver(false);
        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
    >
      {children}
      {over && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 text-sm font-medium text-primary">
          <Upload className="mr-2 size-4" /> {label}
        </div>
      )}
    </div>
  );
}

/** The files a message came with (metadata.resources). */
export function MessageAttachments({ resources }: { resources: { id: string; name: string }[] }) {
  const toast = useToast();
  return (
    <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
      {resources.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => download(r).catch((err) => toast.error(t('Download failed'), errorMessage(err)))}
          className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          title={t('Download')}
        >
          <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{r.name}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- the desk's resources

function ResourceRow({ resource: r, actions }: { resource: Resource; actions: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="flex items-center gap-3 px-5 py-2.5">
      <ResourceIcon mimeType={r.mimeType} className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <button type="button" className="truncate text-left text-sm font-medium hover:underline" title={t('Open {name}', { name: r.name })} onClick={() => setOpen(true)}>
            {r.name}
          </button>
          {r.visibility === 'public' && (
            <Badge tone="green">
              <Globe /> {t('Public')}
            </Badge>
          )}
        </div>
        {open && <FileViewer file={resourceFile(r)} open onOpenChange={setOpen} />}
        <div className={cn('truncate text-xs text-muted-foreground', r.textStatus === 'failed' && 'text-amber-700 dark:text-amber-400')}>
          {formatBytes(r.size)} · {textNote(r)} · <RelativeTime value={r.updatedAt} />
          {r.createdBy.type === 'agent' && r.createdBy.name ? ` · ${t('by {name}', { name: r.createdBy.name })}` : ''}
        </div>
      </div>
      <div className="flex shrink-0 items-center">{actions}</div>
    </li>
  );
}

/**
 * Resources of a desk (docs/features/resources.md): files its agents read (brand guide, price list, product docs),
 * plus the workspace's own files, which every desk reads.
 */
export function DeskResourcesSection({ desk }: { desk: DeskDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const key = qk.resources(desk.workspaceId);
  const resources = useQuery({ queryKey: key, queryFn: () => api.resources.list(desk.workspaceId) });
  const own = (resources.data ?? []).filter((r) => r.deskId === desk.id);
  const shared = (resources.data ?? []).filter((r) => !r.deskId);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const add = async (files: FileList) => {
    setUploading((n) => n + files.length);
    for (const file of Array.from(files)) {
      try {
        await upload(desk.workspaceId, file, { deskId: desk.id });
      } catch (err) {
        toast.error(t('{name} was not added', { name: file.name }), errorMessage(err));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    void refresh();
  };

  const run = async (id: string, fn: () => Promise<unknown>, failure: string) => {
    setBusy(id);
    try {
      await fn();
      void refresh();
    } catch (err) {
      toast.error(failure, errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const downloadButton = (r: Resource) => (
    <Button variant="ghost" size="icon-sm" title={t('Download')} aria-label={t('Download {name}', { name: r.name })} onClick={() => download(r).catch((err) => toast.error(t('Download failed'), errorMessage(err)))}>
      <Download />
    </Button>
  );

  return (
    <Section
      icon={<FolderOpen />}
      title={t('Resources')}
      description={t('Files this desk’s agents read when the work needs them: brand guide, price list, product docs, examples.')}
      actions={
        <>
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void add(e.target.files);
              e.target.value = '';
            }}
          />
          <Button variant="outline" size="sm" onClick={() => input.current?.click()} loading={uploading > 0}>
            {uploading === 0 && <Upload />} {t('Add files')}
          </Button>
        </>
      }
    >
      <DropZone onFiles={(files) => void add(files)} label={t('Drop files to add them to this desk')}>
        {own.length === 0 && !resources.isPending ? (
          <EmptyState compact title={t('No files yet')} description={t('Add files or drop them here (up to 10 MB each). Agents read the text of PDF, Word, HTML and text files.')} />
        ) : (
          <ul className="-mx-5 divide-y border-y">
            {own.map((r) => (
              <ResourceRow
                key={r.id}
                resource={r}
                actions={
                  <>
                    {downloadButton(r)}
                    <Confirm
                      title={t('Delete {name}?', { name: r.name })}
                      description={t('The desk’s agents can no longer read it.')}
                      confirmLabel={t('Delete')}
                      destructive
                      onConfirm={() => run(r.id, () => api.resources.remove(r.id), t('Not deleted'))}
                    >
                      <Button variant="ghost" size="icon-sm" title={t('Delete')} aria-label={t('Delete {name}', { name: r.name })} loading={busy === r.id}>
                        {busy !== r.id && <Trash2 />}
                      </Button>
                    </Confirm>
                  </>
                }
              />
            ))}
          </ul>
        )}
        {shared.length > 0 && (
          <div className="mt-4">
            <div className="mb-1 text-xs font-medium text-muted-foreground">{t('Workspace files (every desk reads them)')}</div>
            <ul className="-mx-5 divide-y border-y">
              {shared.map((r) => (
                <ResourceRow
                  key={r.id}
                  resource={r}
                  actions={
                    <>
                      {downloadButton(r)}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        title={t('Move to this desk: only its agents read it then')}
                        aria-label={t('Move {name} to this desk', { name: r.name })}
                        loading={busy === r.id}
                        onClick={() => void run(r.id, () => api.resources.update(r.id, { deskId: desk.id }), t('Not moved'))}
                      >
                        {busy !== r.id && <ArrowDownToLine />}
                      </Button>
                    </>
                  }
                />
              ))}
            </ul>
          </div>
        )}
      </DropZone>
    </Section>
  );
}
